import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useTenant } from '../contexts/TenantContext';
import { Lead } from '../types';

import LeadDetailModal from '../components/leads/LeadDetailModal';

const Leads: React.FC = () => {
  const { organizationId, workspaceId } = useTenant();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!organizationId || !workspaceId) return;

    // Based on firestore.rules and FirestoreSyncManager.kt analysis
    const leadsRef = collection(db, 'organizations', organizationId, 'workspaces', workspaceId, 'leads');
    const q = query(leadsRef);

    const unsubscribe = onSnapshot(q, (snapshot: any) => {
      const leadsData: Lead[] = [];
      snapshot.forEach((doc: any) => {
        leadsData.push({ id: doc.id, ...doc.data() } as Lead);
      });
      setLeads(leadsData);
      setLoading(false);
    }, (error) => {
      console.error("Error listening to leads:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [organizationId, workspaceId]);

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'phone', headerName: 'Phone', width: 150 },
    {
      field: 'stage',
      headerName: 'Stage',
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color="primary" variant="outlined" />
      )
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'High' ? 'error' : params.value === 'Medium' ? 'warning' : 'default'}
        />
      )
    },
    { field: 'source', headerName: 'Source', width: 120 },
    { field: 'assignedTo', headerName: 'Assigned To', width: 150 },
  ];

  const handleRowClick = (params: any) => {
    setSelectedLead(params.row as Lead);
    setIsModalOpen(true);
  };

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <Typography variant="h4" gutterBottom>Lead Inbox</Typography>
      <Paper elevation={2} sx={{ height: '100%', width: '100%' }}>
        <DataGrid
          rows={leads}
          columns={columns}
          loading={loading}
          onRowClick={handleRowClick}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection
          disableRowSelectionOnClick
          sx={{ border: 0, cursor: 'pointer' }}
        />
      </Paper>
      <LeadDetailModal
        lead={selectedLead}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </Box>
  );
};

export default Leads;
